import { createStyles, makeStyles, Theme } from '@material-ui/core';

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ArchivalCollectionsListContext } from '../context/archival-collections-context';
import CollectionListItem from '../list-items/collection-list-item';

import OriginatorFilterActive from '../originator-filter/originator-filter';
import { AutoSizer } from 'react-virtualized';

import { ArchivalCollection } from 'types';

import { VariableSizeList } from 'react-window';
import { GridUrlParams } from '../types/types';
import { useParams } from 'react-router-dom';
import useMeasure from 'react-use-measure';
import { useDebounce } from 'hooks/debounce-value-hook';

import { LocalSearchInput } from './local-search-field';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    originatorWrapper: {
      display: 'flex',
      flexDirection: 'column',
      color: 'white',
      fontWeight: 'bold',
      fontSize: 16,
      padding: '16px',
    },

    originatorCategoryItemsWrapper: {
      padding: '0 24px',
    },
    originatorCategoryLabel: {
      fontSize: 15,
      marginBottom: -8,
    },
    gridTitle: {
      backgroundColor: theme.palette.secondary.main,
      color: 'white',
      fontWeight: 'bold',
      fontSize: 18,
      height: 70,
      lineHeight: '60px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      zIndex: 1000,
      position: 'sticky',
      top: 0,
      padding: '0 16px',
      width: '100%',
    },
    link: {
      color: theme.palette.grey[300],
    },
    listItem: {
      fontSize: 13,
      lineHeight: '1.5em',
      fontWeight: 500,
      color: theme.palette.grey[300],
    },
    listItemActive: {
      fontSize: 13,
      lineHeight: '1.5em',
      fontWeight: 500,
      color: theme.palette.primary.light,
    },
    nested: {
      paddingLeft: theme.spacing(4),
    },
    subTitle: {
      fontSize: 12,
      lineHeight: 'normal',
      textTransform: 'uppercase',
      fontWeight: 600,
      letterSpacing: '1px',
      color: theme.palette.grey[300],
    },
  })
);

const CollectionList = () => {
  const { items } = useContext(ArchivalCollectionsListContext);
  const { id } = useParams<GridUrlParams>();
  const classes = useStyles();

  const [filter, setFilter] = React.useState<string>('');
  const [filteredData, setFilteredData] = useState<ArchivalCollection[]>(
    items ?? []
  );

  const [rerender, setRerender] = useState(0);

  const [activeRowHeight, setActiveRowHeight] = useState<
    { rowIndex: number; height: number } | undefined
  >();

  const listRef = useRef<VariableSizeList>(null);

  const [wrapperRef, { height }] = useMeasure({
    debounce: 200,
  });

  const debouncedFilter = useDebounce(filter, 300);

  const handleFilterData = useCallback(
    (name: string) => {
      if (name === '' || name === ' ') {
        setRerender((p) => p + 1);
        setFilteredData(items ?? []);
        return;
      }
      setFilteredData(
        (items ?? []).filter((item) =>
          (item?.name ?? '').toLowerCase().includes(name.toLowerCase())
        )
      );
    },
    [items]
  );

  useEffect(() => {
    handleFilterData(debouncedFilter);
  }, [debouncedFilter, handleFilterData]);

  if (!items) {
    return <></>;
  }

  return (
    <div>
      <div
        className={classes.gridTitle}
        style={{
          display: 'flex',
          justifyContent: 'space-between ',
          alignItems: 'center',
        }}
      >
        Fondy
        <LocalSearchInput filter={filter} setFilter={setFilter} />
      </div>

      <OriginatorFilterActive ref={wrapperRef} />
      <div
        key={`collection-list-${activeRowHeight?.rowIndex}-${rerender}`}
        style={{
          height: `calc(100vh - ${150 + height}px)`,
          width: '100%',
          position: 'relative',
        }}
      >
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {
            //@ts-ignore
            ({ width, height }) => {
              return (
                <VariableSizeList
                  ref={listRef}
                  height={height}
                  width={width}
                  style={{ scrollBehavior: 'auto' }}
                  initialScrollOffset={
                    24.99 *
                    Math.max(
                      0,
                      filteredData.findIndex((item) => item.id === id)
                    )
                  }
                  itemCount={(filteredData ?? []).length}
                  itemSize={(index) => {
                    return activeRowHeight?.rowIndex === index
                      ? activeRowHeight.height
                      : 25;
                  }}
                >
                  {({ index, style }) => (
                    <div key={index} style={style}>
                      <CollectionListItem
                        key={`collection-item-${activeRowHeight?.rowIndex}-${rerender}`}
                        collection={filteredData[index]}
                        itemIndex={index}
                        activeRowIndex={activeRowHeight?.rowIndex ?? -1}
                        setHeight={(height) => {
                          setActiveRowHeight({ rowIndex: index, height });
                          listRef?.current?.resetAfterIndex?.(0, true);
                          setRerender((p) => p + 1);
                        }}
                        height={activeRowHeight?.height ?? 25}
                      />
                    </div>
                  )}
                </VariableSizeList>
              );
            }
          }
        </AutoSizer>
      </div>
    </div>
  );
};

export default CollectionList;
